import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { SiteLayout, TrialLink } from "./SiteLayout";
import { FoundingCounter } from "./MarketingPages";

const GRAMS_PER_LB = 453.59237;

export type PayInputs = { crew: number; hours: number; gph: number; hourly: number; perLb: number; base: number; bonusPerLb: number };
type Model = { key: string; name: string; costPerLb: number; perPersonDay: number; effectiveHourly: number };

/** Labour cost per pound and trimmer earnings under hourly, per-pound and hybrid pay. */
export function payModels(i: PayInputs): { lbPerPersonDay: number; models: Model[]; breakEvenGph: number | null } {
  const lbPerPersonDay = (i.gph * i.hours) / GRAMS_PER_LB;
  const model = (key: string, name: string, perPersonDay: number): Model => ({
    key, name, perPersonDay,
    costPerLb: lbPerPersonDay > 0 ? perPersonDay / lbPerPersonDay : 0,
    effectiveHourly: i.hours > 0 ? perPersonDay / i.hours : 0,
  });
  return {
    lbPerPersonDay,
    models: [
      model("hourly", "Hourly", i.hourly * i.hours),
      model("per-lb", "Per pound", i.perLb * lbPerPersonDay),
      model("hybrid", "Base + per-pound bonus", i.base * i.hours + i.bonusPerLb * lbPerPersonDay),
    ],
    // The pace at which per-pound pay costs the same as hourly pay.
    breakEvenGph: i.perLb > 0 ? (i.hourly / i.perLb) * GRAMS_PER_LB : null,
  };
}

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Field({ id, label, hint, value, onChange, step = 1 }: { id: string; label: string; hint?: string; value: number; onChange: (n: number) => void; step?: number }) {
  return <label className="calc-field" htmlFor={id}><span>{label}</span>
    <input id={id} type="number" inputMode="decimal" min="0" step={step} value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))} />
    {hint && <small>{hint}</small>}</label>;
}

export function TrimPayCalculatorPage() {
  const [i, setI] = useState<PayInputs>({ crew: 6, hours: 8, gph: 55, hourly: 20, perLb: 150, base: 15, bonusPerLb: 50 });
  const set = (key: keyof PayInputs) => (n: number) => setI({ ...i, [key]: n });
  const { lbPerPersonDay, models, breakEvenGph } = payModels(i);
  const cheapest = models.reduce((a, b) => (b.costPerLb < a.costPerLb ? b : a));
  return <SiteLayout title="Trim pay calculator: hourly vs per pound | Trim Log" description="Compare hourly, per-pound and hybrid pay for a cannabis trim crew. See labour cost per pound, what each trimmer earns, and the pace where piece rate breaks even.">
    <main id="main-content">
      <div className="site-page-heading"><h1>Trim pay<br />calculator.</h1><p>Hourly, per pound, or a base with a bonus? Put in your crew's pace and rates and compare labour cost per pound with what each trimmer takes home.</p></div>
      <section className="calc-layout" aria-label="Trim pay calculator">
        <div className="calc-inputs">
          <h2>Your room</h2>
          <Field id="calc-crew" label="Trimmers" value={i.crew} onChange={set("crew")} />
          <Field id="calc-hours" label="Hours per day" value={i.hours} onChange={set("hours")} step={0.5} />
          <Field id="calc-gph" label="Average pace (grams per active hour, per person)" hint="Don't know it? That's the number Trim Log measures for you." value={i.gph} onChange={set("gph")} />
          <h2>Pay rates</h2>
          <Field id="calc-hourly" label="Hourly rate ($/hour)" value={i.hourly} onChange={set("hourly")} step={0.5} />
          <Field id="calc-perlb" label="Piece rate ($/lb)" value={i.perLb} onChange={set("perLb")} step={5} />
          <Field id="calc-base" label="Hybrid base ($/hour)" value={i.base} onChange={set("base")} step={0.5} />
          <Field id="calc-bonus" label="Hybrid bonus ($/lb)" value={i.bonusPerLb} onChange={set("bonusPerLb")} step={5} />
        </div>
        <div className="calc-results" aria-live="polite">
          <p className="calc-output-line"><strong>{lbPerPersonDay.toFixed(2)} lb</strong> per trimmer per day · <strong>{(lbPerPersonDay * i.crew).toFixed(2)} lb</strong> for the crew</p>
          <div className="calc-table-wrap"><table className="calc-table">
            <thead><tr><th>Pay model</th><th>Cost per lb</th><th>Trimmer per day</th><th>Effective hourly</th><th>Crew per day</th></tr></thead>
            <tbody>{models.map((m) => <tr key={m.key} className={m.key === cheapest.key ? "is-lowest" : undefined}>
              <th scope="row">{m.name}{m.key === cheapest.key && <span className="calc-tag">lowest cost</span>}</th>
              <td data-label="Cost per lb">{money(m.costPerLb)}</td><td data-label="Trimmer per day">{money(m.perPersonDay)}</td><td data-label="Effective hourly">{money(m.effectiveHourly)}</td><td data-label="Crew per day">{money(m.perPersonDay * i.crew)}</td>
            </tr>)}</tbody>
          </table></div>
          {breakEvenGph !== null && <p className="calc-note">Per-pound pay costs the same as hourly at <strong>{breakEvenGph.toFixed(1)} g/hour</strong>. Trimmers faster than that earn more on piece rate; slower ones earn more on hourly.</p>}
          <p className="calc-note">Every figure above depends on pace. Trim Log gives each trimmer their own run timer and records grams per active hour by person, strain and harvest, so pay is based on what actually happened.</p>
          <FoundingCounter />
          <div className="hero-actions"><TrialLink /><Link to="/tour" className="site-text-link">See how pace is tracked <span aria-hidden="true">↗</span></Link></div>
        </div>
      </section>
    </main>
  </SiteLayout>;
}
